import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import {
  Car,
  CheckCircle2,
  KeySquare,
  AlertTriangle,
  DollarSign,
  CalendarClock,
  ReceiptText,
  TrendingUp,
  Activity,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import RemindersWidget from "@/components/RemindersWidget";
import RenewalsThisWeek from "@/components/RenewalsThisWeek";
import RentalsDueList from "@/components/RentalsDueList";

const Kpi = ({ icon: Icon, label, value, hint, accent = "text-black", testId }) => (
  <div className="kpi-card" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className={`mt-2 font-heading text-3xl font-bold tracking-tight tabular-nums ${accent}`}>{value}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </div>
      <div className="w-9 h-9 rounded-md bg-gray-100 grid place-items-center">
        <Icon size={18} className="text-gray-700" strokeWidth={2} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="p-6 lg:p-8">
        <div className="text-sm text-gray-500">Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-6 lg:mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-1">Overview</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Live snapshot of your fleet, rentals and revenue.</p>
        </div>
        <div className="pill pill-gray" data-testid="dashboard-utilization">
          <Activity size={12} />
          Fleet utilization {data.fleet_utilization}%
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        <Kpi testId="kpi-total-vehicles" icon={Car} label="Total Vehicles" value={data.total_vehicles} />
        <Kpi testId="kpi-available" icon={CheckCircle2} label="Available" value={data.available_vehicles} accent="text-green-700" />
        <Kpi testId="kpi-rented" icon={KeySquare} label="Currently Rented" value={data.rented_vehicles} accent="text-blue-700" />
        <Kpi testId="kpi-overdue" icon={AlertTriangle} label="Overdue Rentals" value={data.overdue_rentals} accent={data.overdue_rentals ? "text-red-700" : ""} />
        <Kpi testId="kpi-payments-due" icon={DollarSign} label="Weekly Outstanding" value={money(data.payments_due)} accent={data.payments_due ? "text-amber-700" : ""} />
        <Kpi testId="kpi-upcoming-returns" icon={CalendarClock} label="Upcoming Returns (7d)" value={data.upcoming_returns} />
        <Kpi testId="kpi-open-tolls" icon={ReceiptText} label="Open Tolls/Tickets" value={data.open_tolls} hint={data.open_tolls_amount ? money(data.open_tolls_amount) : null} />
        <Kpi testId="kpi-monthly-revenue" icon={TrendingUp} label="Monthly Revenue (projected)" value={money(data.monthly_revenue)} accent="text-green-700" hint={`${data.rented_vehicles} rented × 4 wks`} />
      </div>

      <div className="mt-6 lg:mt-8">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="overline mb-1">Today's outreach</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Send reminders from your phone</h2>
            <p className="text-xs text-gray-500 mt-1">Tap "Text" to open iMessage with a pre-filled message — sends from your real number.</p>
          </div>
        </div>
        <RemindersWidget />
      </div>

      <div className="mt-6 lg:mt-8">
        <RenewalsThisWeek />
      </div>

      <div className="mt-6 lg:mt-8">
        <RentalsDueList />
      </div>

      <div className="mt-6 lg:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Revenue Trend</div>
              <h3 className="font-heading text-lg font-semibold">Last 6 months</h3>
            </div>
          </div>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenue_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", color: "white", border: "none", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => money(v)}
                />
                <Bar dataKey="revenue" fill="#0a0a0a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="overline">Fleet Status</div>
          <h3 className="font-heading text-lg font-semibold mb-3">Vehicle breakdown</h3>
          <div className="space-y-2.5">
            {Object.entries(data.status_breakdown).map(([k, v]) => {
              const total = data.total_vehicles || 1;
              const pct = Math.round((v / total) * 100);
              const color =
                k === "Available"
                  ? "bg-green-500"
                  : k === "Rented"
                    ? "bg-blue-600"
                    : k === "Reserved"
                      ? "bg-amber-500"
                      : k === "Maintenance"
                        ? "bg-orange-500"
                        : "bg-gray-400";
              return (
                <div key={k}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{k}</span>
                    <span className="font-medium tabular-nums">{v}</span>
                  </div>
                  <div className="h-1.5 mt-1 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
